import React, { useState, useEffect, FormEvent } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { DollarSign, Heart } from 'react-feather';
import VisitorLayout from '../../components/layouts/VisitorLayout';
import PaymentService from '../../services/payment.service';
import CoupleService from '../../services/couple.service';
import { toast } from 'react-toastify';
import {
  formatPrice,
  formatPriceSimple,
  CURRENCY_SYMBOLS,
  getAmountInMinorUnit,
} from '../../utils/currency';
import { Couple } from '../../types';

const ContributionPage: React.FC = () => {
  const { customUrl } = useParams<{ customUrl: string }>();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'wedding_fund';
  const giftId = searchParams.get('giftId') || '';
  const registryItem = searchParams.get('item') || '';

  const [couple, setCouple] = useState<Couple | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [contributorName, setContributorName] = useState('');
  const [contributorEmail, setContributorEmail] = useState('');
  const [amount, setAmount] = useState(1000);
  const [message, setMessage] = useState('');
  // Fixed currency to NGN only
  const currency = 'NGN';

  useEffect(() => {
    const fetchCouple = async () => {
      if (!customUrl) {
        setLoading(false);
        setError('No couple information provided');
        return;
      }

      try {
        setLoading(true);
        console.log('Fetching couple with custom URL:', customUrl);
        const response = await CoupleService.getCoupleByCustomUrl(customUrl);
        console.log('Couple data received:', response);
        setCouple(response);

        localStorage.setItem('contributionCoupleUrl', customUrl);
      } catch (err) {
        console.error('Error fetching couple:', err);
        setError('Could not load couple information.');
      } finally {
        setLoading(false);
      }
    };

    // Clear stale contribution data
    localStorage.removeItem('isContribution');
    localStorage.removeItem('paystack_reference');
    localStorage.removeItem('contributionDetails');

    fetchCouple();
  }, [customUrl]);

  const getContributionTypeText = () => {
    switch (type) {
      case 'wedding_fund':
        return 'Wedding Fund';
      case 'honeymoon':
        return 'Honeymoon Fund';
      case 'gift':
        return registryItem || 'Gift';
      default:
        return 'Contribution';
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!contributorName.trim()) {
      toast.error('Please enter your name.');
      return;
    }

    if (amount < 100) {
      toast.error('Amount must be at least 100 Naira.');
      return;
    }

    if (!couple || !couple._id) {
      toast.error('Couple information is missing. Please refresh the page.');
      return;
    }

    try {
      setSubmitting(true);

      const amountInMinorUnit = getAmountInMinorUnit(amount, currency);

      // Create transaction data object
      const transactionData = {
        amount: amountInMinorUnit,
        email: contributorEmail || `guest-${Date.now()}@example.com`,
        currency: currency,
        metadata: {
          type: 'contribution', // Fixed to match backend expectation
          contributionType: type, // Store dynamic type (wedding_fund, honeymoon, gift)
          couple: couple._id,
          contributorName,
          contributorEmail: contributorEmail || undefined,
          message: message || undefined,
          giftId: giftId || undefined,
          registryItem: registryItem || undefined,
          currency: currency,
        },
      };

      console.log('Initializing payment with data:', transactionData);

      // Initialize payment
      const paymentResponse = await PaymentService.publicInitializeTransaction(transactionData);

      console.log('Payment initialization response:', paymentResponse);

      // Check for authorization URL and redirect
      if (paymentResponse?.data?.authorization_url) {
        // Store contribution details
        localStorage.setItem('contributionCoupleUrl', customUrl || '');
        localStorage.setItem('isContribution', 'true');
        localStorage.setItem('paystack_reference', paymentResponse.data.reference);

        try {
          localStorage.setItem(
            'contributionDetails',
            JSON.stringify({
              amount: amount,
              currency: currency,
              contributorName: contributorName,
              type: getContributionTypeText(),
            })
          );
        } catch (e) {
          console.warn('Could not store contribution details:', e);
        }

        // Append isContribution=true to the authorization URL
        const redirectUrl = new URL(paymentResponse.data.authorization_url);
        redirectUrl.searchParams.append('isContribution', 'true');
        window.location.href = redirectUrl.toString();
      } else {
        throw new Error('Could not initialize payment. Invalid response from server.');
      }
    } catch (err: any) {
      console.error('Payment initialization error:', err);
      toast.error(err.response?.data?.message || 'Failed to process your contribution. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <VisitorLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          <p className="ml-3 text-gray-600">Loading couple information...</p>
        </div>
      </VisitorLayout>
    );
  }

  if (error || !couple) {
    return (
      <VisitorLayout>
        <div className="py-10 text-center">
          <div className="mb-6">
            <span className="inline-block bg-red-100 p-3 rounded-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Couple Not Found</h1>
          <p className="text-gray-600 mb-8">{error || "We couldn't find the couple you're looking for."}</p>
          <Link
            to="/"
            className="bg-pink-500 text-white px-6 py-3 rounded-full font-medium hover:bg-pink-600 transition-colors"
          >
            Return to Homepage
          </Link>
        </div>
      </VisitorLayout>
    );
  }

  const bridesName = couple.bridesName || couple.names?.split('&')?.[0]?.trim() || 'The Couple';
  const groomsName = couple.groomsName || couple.names?.split('&')?.[1]?.trim() || '';

  return (
    <VisitorLayout>
      <div className="max-w-4xl mx-auto py-10 px-4">
        <div className="text-center mb-10">
          <div className="mb-4">
            <span className="inline-block bg-pink-100 p-3 rounded-full">
              <Heart className="h-8 w-8 text-pink-500" />
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-3">
            Send a Gift to {bridesName}
            {groomsName ? ` & ${groomsName}` : ''}
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Your contribution to the {getContributionTypeText().toLowerCase()} will help make their special day even more
            memorable.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit}>
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-2">Contribution Details</h2>
              <p className="text-gray-600 text-sm">
                You're contributing to: <span className="font-medium">{getContributionTypeText()}</span>
              </p>
            </div>

            <div className="mb-6">
              <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                Your Name *
              </label>
              <input
                type="text"
                id="name"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 !text-gray-900"
                placeholder="Enter your name"
                value={contributorName}
                onChange={(e) => setContributorName(e.target.value)}
                required
              />
            </div>

            <div className="mb-6">
              <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                Your Email (Optional)
              </label>
              <input
                type="email"
                id="email"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 !text-gray-900"
                placeholder="Enter your email"
                value={contributorEmail}
                onChange={(e) => setContributorEmail(e.target.value)}
              />
              <p className="text-gray-500 text-sm mt-1">Add your email to receive a thank you note</p>
            </div>

            <div className="mb-6">
              <div className="flex items-center">
                <div className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg font-medium border border-gray-200">
                  Currency: Nigerian Naira (₦)
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="amount" className="block text-gray-700 font-medium mb-2">
                Amount (₦) *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-700 font-medium">₦</span>
                </div>
                <input
                  type="number"
                  id="amount"
                  className="w-full pl-8 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 !text-gray-900"
                  placeholder="Enter amount"
                  min="100"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  required
                />
              </div>
              <p className="text-gray-500 text-sm mt-1">Minimum amount: ₦100</p>
            </div>

            <div className="mb-8">
              <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                Message (Optional)
              </label>
              <textarea
                id="message"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 !text-gray-900"
                placeholder="Add a personal message..."
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              ></textarea>
            </div>

            <div className="text-center mt-8">
              <div className="mb-4">
                <p className="text-gray-700 font-bold text-2xl">₦{amount.toLocaleString()}</p>
              </div>
              <button
                type="submit"
                className={`w-full py-4 px-6 rounded-full text-white font-medium focus:outline-none transition-colors ${
                  submitting ? 'bg-pink-400 cursor-not-allowed' : 'bg-pink-600 hover:bg-pink-700'
                }`}
                disabled={submitting}
              >
                {submitting ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <DollarSign className="h-5 w-5 mr-2" />
                    Send Contribution
                  </span>
                )}
              </button>
              <p className="text-gray-500 text-sm mt-4">You'll be redirected to our secure payment provider.</p>
            </div>
          </form>
        </div>
      </div>
    </VisitorLayout>
  );
};

export default ContributionPage;